'use client'

import ms from 'ms'
import { AuthButton } from '@repo/studio-components/auth/auth-button'
import { Z_INDEX_MODALS } from 'constants/modals'
import { animate, domMax, LazyMotion, m, useMotionValue } from 'framer-motion'
import * as React from 'react'
import { tw } from 'utils/classnames'
import {
  FLOATING_BUTTON_LG_MIN_WIDTH_PX,
  useFloatingButtonPosition,
} from '../../hooks/use-floating-button-position'
import { openAidaOverlayFromElement } from '../../stores/overlay-store'
import { useAidaImageDrop } from '../../hooks/use-aida-image-drop'
import { AidaIconAnimated } from '../icon/aida-icon-animated'
import { AidaBubbleDropFrame } from './aida-image-drop-chrome'
import { AidaGeneratingRing } from './aida-generating-ring'
import { AidaPeekTab } from './aida-peek-tab'

const SNAP_DURATION_S = ms('280ms') / ms('1s')
const SNAP_EASE: [number, number, number, number] = [0.22, 1, 0.36, 1]

function openAidaFromClick(event: React.MouseEvent<HTMLButtonElement>) {
  openAidaOverlayFromElement(event.currentTarget)
}

function FloatingButtonFace({ loggedIn, generating }: { loggedIn: boolean; generating: boolean }) {
  const button = (
    <button
      type="button"
      aria-label="Chat with Aida"
      onClick={loggedIn ? openAidaFromClick : undefined}
      className={tw(
        'relative inline-flex size-full items-center justify-center rounded-full',
        'border border-gray-100 bg-service-white-white shadow-lg',
        'cursor-pointer',
      )}
    >
      <AidaIconAnimated aurora className="size-4 lg:size-6" />
      {generating ? <AidaGeneratingRing /> : null}
    </button>
  )

  if (loggedIn) {
    return button
  }

  return <AuthButton>{button}</AuthButton>
}

function useIsPeekViewport() {
  const query = `(max-width: ${FLOATING_BUTTON_LG_MIN_WIDTH_PX - 1}px)`
  const [isPeek, setIsPeek] = React.useState(
    () => typeof window !== 'undefined' && window.matchMedia(query).matches,
  )

  React.useEffect(() => {
    const media = window.matchMedia(query)
    function onChange() {
      setIsPeek(media.matches)
    }
    onChange()
    media.addEventListener('change', onChange)
    return () => media.removeEventListener('change', onChange)
  }, [query])

  return isPeek
}

export function AidaFloatingButton({
  loggedIn,
  generating,
}: {
  loggedIn: boolean
  generating: boolean
}) {
  const isPeek = useIsPeekViewport()
  if (isPeek) {
    return <AidaPeekTab loggedIn={loggedIn} generating={generating} />
  }

  return <AidaFreeFloatingButton loggedIn={loggedIn} generating={generating} />
}

function AidaFreeFloatingButton({
  loggedIn,
  generating,
}: {
  loggedIn: boolean
  generating: boolean
}) {
  const imageDrop = useAidaImageDrop()
  const { xy, persistDragEnd, commitPosition, viewport, buttonSize } = useFloatingButtonPosition()
  const x = useMotionValue(xy.x)
  const y = useMotionValue(xy.y)
  const justDraggedRef = React.useRef(false)
  const dragEndTokenRef = React.useRef(0)
  const isAnimatingRef = React.useRef(false)

  React.useEffect(() => {
    if (isAnimatingRef.current) {
      return
    }
    x.set(xy.x)
    y.set(xy.y)
  }, [xy.x, xy.y, x, y])

  const dragConstraints = React.useMemo(
    () => ({
      left: 0,
      top: 0,
      right: Math.max(0, viewport.width - buttonSize),
      bottom: Math.max(0, viewport.height - buttonSize),
    }),
    [buttonSize, viewport.height, viewport.width],
  )

  return (
    <LazyMotion features={domMax}>
      <m.div
        ref={imageDrop.originRef}
        data-testid="aida-floating-button"
        data-aida-floating-button
        drag
        dragMomentum={false}
        dragConstraints={dragConstraints}
        dragElastic={0}
        style={{ x, y, zIndex: Z_INDEX_MODALS.default }}
        className="fixed top-0 left-0 size-10 touch-none lg:size-16"
        onDragEnter={imageDrop.onDragEnter}
        onDragOver={imageDrop.onDragOver}
        onDragLeave={imageDrop.onDragLeave}
        onDrop={imageDrop.onDrop}
        onDragStart={() => {
          justDraggedRef.current = true
          isAnimatingRef.current = false
        }}
        onDragEnd={async () => {
          const token = ++dragEndTokenRef.current
          const { position: nextPosition, target } = persistDragEnd({
            x: x.get(),
            y: y.get(),
          })
          isAnimatingRef.current = true
          const animationX = animate(x, target.x, {
            duration: SNAP_DURATION_S,
            ease: SNAP_EASE,
          })
          const animationY = animate(y, target.y, {
            duration: SNAP_DURATION_S,
            ease: SNAP_EASE,
          })
          setTimeout(() => {
            justDraggedRef.current = false
          }, ms('50ms'))
          try {
            await Promise.all([animationX.finished, animationY.finished])
          } catch {
            return
          }
          if (token !== dragEndTokenRef.current) {
            return
          }
          isAnimatingRef.current = false
          commitPosition(nextPosition)
        }}
      >
        <AidaBubbleDropFrame
          available={imageDrop.available}
          over={imageDrop.isOver}
          className="size-full"
        >
          <div
            className="size-full"
            onClickCapture={event => {
              if (justDraggedRef.current) {
                event.preventDefault()
                event.stopPropagation()
              }
            }}
          >
            <FloatingButtonFace loggedIn={loggedIn} generating={generating} />
          </div>
        </AidaBubbleDropFrame>
      </m.div>
    </LazyMotion>
  )
}
